import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  ArrowLeft, ArrowRight, TrendingUp, TrendingDown, Minus, 
  Calendar, Trophy, Loader2, GitCompare
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompareGames = ({ onBack }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [session1Id, setSession1Id] = useState('');
  const [session2Id, setSession2Id] = useState('');
  const [comparison, setComparison] = useState(null);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/game-sessions?limit=20`);
      const completedSessions = (response.data.sessions || []).filter(s => s.is_complete);
      setSessions(completedSessions);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompare = async () => {
    if (!session1Id || !session2Id) return;
    
    setComparing(true);
    try {
      const response = await axios.get(
        `${API}/player/compare-games?session1_id=${session1Id}&session2_id=${session2Id}`
      );
      setComparison(response.data);
    } catch (error) {
      console.error('Failed to compare games:', error);
    } finally {
      setComparing(false);
    }
  };

  const getImprovementIcon = (improvement) => {
    switch (improvement) {
      case 'better':
        return <TrendingUp className="w-4 h-4 text-green-400" />;
      case 'worse':
        return <TrendingDown className="w-4 h-4 text-red-400" />;
      default:
        return <Minus className="w-4 h-4 text-slate-400" />;
    }
  };

  const getImprovementColor = (improvement) => {
    switch (improvement) {
      case 'better':
        return 'text-green-400';
      case 'worse':
        return 'text-red-400';
      default:
        return 'text-slate-400';
    }
  };

  const formatDiff = (diff) => {
    if (diff > 0) return `+${diff}`;
    return diff.toString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <GitCompare className="w-5 h-5 text-amber-400" />
            Compare Games
          </h1>
        </div>

        {/* Selection Card */}
        <Card className="bg-slate-800/90 border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-white">Select Games to Compare</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Game 1 */}
              <div className="space-y-2">
                <label className="text-sm text-slate-400">First Game (Earlier)</label>
                <Select value={session1Id} onValueChange={setSession1Id}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue placeholder="Select game" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600 max-h-[200px]">
                    {sessions.map((s) => (
                      <SelectItem 
                        key={s.id} 
                        value={s.id} 
                        className="text-white hover:bg-slate-600"
                        disabled={s.id === session2Id}
                      >
                        {s.game_date} vs {s.opponent_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Game 2 */}
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Second Game (Later)</label>
                <Select value={session2Id} onValueChange={setSession2Id}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue placeholder="Select game" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600 max-h-[200px]">
                    {sessions.map((s) => (
                      <SelectItem 
                        key={s.id} 
                        value={s.id} 
                        className="text-white hover:bg-slate-600"
                        disabled={s.id === session1Id}
                      >
                        {s.game_date} vs {s.opponent_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              onClick={handleCompare}
              disabled={!session1Id || !session2Id || comparing}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
            >
              {comparing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <GitCompare className="w-4 h-4 mr-2" />
              )}
              Compare Games
            </Button>
          </CardContent>
        </Card>

        {/* Comparison Results */}
        {comparison && (
          <>
            {/* Game Info Cards */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-slate-800/90 border-slate-700">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-400 text-sm">{comparison.game1.date}</span>
                  </div>
                  <p className="text-white font-medium truncate">vs {comparison.game1.opponent}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className="bg-slate-700 text-slate-300 capitalize">
                      {comparison.game1.position?.replace('_', ' ')}
                    </Badge>
                    <Badge className={`${
                      comparison.game1.grade?.startsWith('A') ? 'bg-green-500' :
                      comparison.game1.grade?.startsWith('B') ? 'bg-blue-500' :
                      'bg-yellow-500'
                    } text-white`}>
                      {comparison.game1.grade}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/90 border-slate-700">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-400 text-sm">{comparison.game2.date}</span>
                  </div>
                  <p className="text-white font-medium truncate">vs {comparison.game2.opponent}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className="bg-slate-700 text-slate-300 capitalize">
                      {comparison.game2.position?.replace('_', ' ')}
                    </Badge>
                    <Badge className={`${
                      comparison.game2.grade?.startsWith('A') ? 'bg-green-500' :
                      comparison.game2.grade?.startsWith('B') ? 'bg-blue-500' :
                      'bg-yellow-500'
                    } text-white`}>
                      {comparison.game2.grade}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Summary */}
            <Card className="bg-gradient-to-r from-slate-800 to-slate-800/50 border-slate-700">
              <CardContent className="py-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-green-400">{comparison.summary.improved_stats}</p>
                    <p className="text-xs text-slate-400">Improved</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-400">{comparison.summary.unchanged_stats}</p>
                    <p className="text-xs text-slate-400">Same</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-400">{comparison.summary.declined_stats}</p>
                    <p className="text-xs text-slate-400">Declined</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stat Comparison Table */}
            <Card className="bg-slate-800/90 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-white">Stat Comparison</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {/* Header */}
                  <div className="grid grid-cols-4 gap-2 text-xs text-slate-400 pb-2 border-b border-slate-700">
                    <span>Stat</span>
                    <span className="text-center">Game 1</span>
                    <span className="text-center">Game 2</span>
                    <span className="text-center">Change</span>
                  </div>

                  {/* Stats */}
                  {comparison.comparison
                    .filter(c => c.game1_value > 0 || c.game2_value > 0)
                    .map((stat) => (
                    <div 
                      key={stat.stat_key}
                      className="grid grid-cols-4 gap-2 items-center py-2 border-b border-slate-700/50"
                    >
                      <span className="text-slate-300 text-sm truncate">
                        {stat.stat_label}
                      </span>
                      <span className="text-center text-white font-medium">
                        {stat.game1_value}
                      </span>
                      <span className="text-center text-white font-medium">
                        {stat.game2_value}
                      </span>
                      <div className="flex items-center justify-center gap-1">
                        {getImprovementIcon(stat.improvement)}
                        <span className={`text-sm font-medium ${getImprovementColor(stat.improvement)}`}>
                          {formatDiff(stat.difference)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {sessions.length < 2 && (
          <Card className="bg-slate-800/90 border-slate-700">
            <CardContent className="py-8 text-center">
              <Trophy className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">
                Complete at least 2 game sessions to compare your performance
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CompareGames;
